import { Chip, MenuItem, tooltipClasses } from "@mui/material";
import { GridColDef, GridRowId } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbProgress } from "react-icons/tb";

import Dropdown from "@/components/Dropdown";
import { TableHeader } from "@/components/TableHeader";
import { TASK_STATUS } from "@/constants";
import { TaskStatuses } from "@/constants/status";
import { TaskStatusItem, taskStatuses } from "@/constants/taskStatuses";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { useTrackingActions } from "@/stores/use-tracking-table";
import { Task } from "@/types/tasks.types";

function TrackingStatusColumn({ t }: { t: TFunction }): GridColDef<Task> {
    return {
        field: "status",
        headerName: t("status"),
        minWidth: 210,
        flex: 0.5,
        filterable: false,
        valueGetter: (_val, row) => row.status,
        renderHeader: (params) => <TableHeader Component={TbProgress} title={params.colDef.headerName!} />,
        renderCell: ({ value, id }) => <TrackingStatusCell value={value} id={id} />,
        getApplyQuickFilterFn: (value: string) => {
            if (!value) return () => true;

            const searchText = value.toLowerCase();

            return (val, row) => {
                const matches = (target: string) =>
                    target?.toLowerCase().localeCompare(searchText, undefined, { sensitivity: "base" }) === 0 ||
                    target?.toLowerCase().includes(searchText);

                const matchesRowValue = matches(val?.toString());

                const matchesPassengerOrCompanion = row.passengers?.some(
                    ({ name, companions, flightNumber }) =>
                        matches(name) || matches(flightNumber) || companions?.some((companion) => matches(companion))
                );

                return matchesRowValue || matchesPassengerOrCompanion;
            };
        },
    };
}

function MenuItemList({ value, id, handleClose }: { value: TaskStatuses; id: GridRowId; handleClose: () => void }) {
    const { updateShowTaskUpdateDialog } = useTrackingActions();
    const handleClick = (status: TaskStatuses) => {
        handleClose();
        updateShowTaskUpdateDialog({
            id: id.toString(),
            current: value,
            next: status,
        });
    };

    return taskStatuses.map(
        (item) =>
            value !== item.status &&
            !(item.status === TaskStatuses.DONE && value !== TaskStatuses.ON_ROUTE) && (
                <MenuItem onClick={() => handleClick(item.status)} key={item.status}>
                    <StatusChip {...item} />
                </MenuItem>
            )
    );
}

export function TrackingStatusCell({ value, id }: { value: TaskStatuses; id: GridRowId }) {
    const status = useMemo(() => taskStatuses.find((item) => item.status === value), [value]);
    const content = useCallback(
        ({ handleClose }: { handleClose: () => void }) => (
            <MenuItemList handleClose={handleClose} value={value} id={id} />
        ),
        [value, id]
    );

    return (
        <div className="w-full h-full pl-2 py-1 flex items-center">
            {status && (
                <Dropdown content={content} title="Görev Durumu">
                    <StatusChip {...status} />
                </Dropdown>
            )}
        </div>
    );
}

function StatusChip(status: TaskStatusItem) {
    const { t } = useTranslation();

    return (
        <NoMaxWidthTooltip
            arrow
            sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: "white",
                    border: "none",
                    padding: "0px !important",
                    fontSize: 12,
                    maxWidth: "200px !important",
                },
            }}
            title={<HotelStatusTooltip status={status} />}
        >
            <Chip
                sx={{
                    backgroundColor: status.bg,
                    color: status.text,
                    fontWeight: 500,
                    fontSize: "12px !important",
                }}
                icon={createElement(status.component(), { fontSize: 14, color: status.text })}
                label={t(TASK_STATUS[status.status as keyof typeof TASK_STATUS])}
            />
        </NoMaxWidthTooltip>
    );
}

export default TrackingStatusColumn;
